import { datalabWithTokenHttp, type TDatalabHttpConfig } from '@dbl/services/common';

import {
  transformChartDetailDtoToVo,
  transformChartListResponseDtoToVo,
} from './transformers';
import type {
  IChartDetailDto,
  IChartDetailVo,
  IGetChartDetailRequest,
  IGetChartListRequest,
  IGetChartListResponseDto,
  IGetChartListResponseVo,
} from './types';

/**
 * 图表任务读方向（Layer 4）：`getChartDetail` · `getChartList`。
 *
 * 写方向见 `services/chart/saveChart/`。
 */

/**
 * 拉取图表任务详情（`getChartDetail`）。
 *
 * ## 为何独立 `services/chart/chartDetail/`
 *
 * - 详情契约体量大（全量 DTO + 多双态 JSON 字段 + 嵌套 VO），transform 步骤多；
 * - 跨多维分析、探索分析、AB 实验等页面的核心读模型；
 * - 与 `saveChart` / `calculation` 同级独立子模块。
 *
 * ### wire DTO（存量调用方）
 *
 * 重构迁移完成后删除，统一改用 {@link getChartDetailVo}。
 *
 * 无上传 prepare：请求体为 snake_case {@link IGetChartDetailRequest}（`task_id` / `game_code`），
 * 无双态 JSON stringify、无上传 VO。写方向 prepare 见 `saveChart/`。
 */
export const getChartDetail = (
  props: IGetChartDetailRequest,
): Promise<IChartDetailDto> =>
  datalabWithTokenHttp({
    url: '/api/v1/datalab/getChartDetail',
    method: 'post',
    data: props,
  } as TDatalabHttpConfig);

/**
 * 拉取图表任务详情（前端 VO，完整 transformer 模板）。
 *
 * HTTP 返回后在边界层执行 DTO→VO（`transformChartDetailDtoToVo`）：
 * - 双态 JSON 字符串 parse
 * - snake_case → camelCase
 * - wire 空值 / 非法结构归一
 * - 剔除无消费方字段（见 `types.ts` · IChartDetailDto 审计清单）
 *
 */
export const getChartDetailVo = async (
  props: IGetChartDetailRequest,
): Promise<IChartDetailVo> => {
  const dto = await getChartDetail(props);
  return transformChartDetailDtoToVo(dto);
};

/**
 * 查询图表任务列表（我的分析、工作台建报表、报表布局等）。
 *
 * 原始实现：`src/pages/datalab/pages/MyAnalyze/service.ts` -> getChartList
 *
 * `data_list` 项 wire 与 {@link IChartDetailDto} 同构。
 *
 * 无上传 prepare：L1 已按 wire 组装 {@link IGetChartListRequest}（含 `sub_labels` 等），
 * 无双态 JSON stringify、无 camelCase 上传 VO。写方向 prepare 见 `saveChart/`。
 */
export const getChartList = (
  props: IGetChartListRequest,
): Promise<IGetChartListResponseDto> =>
  datalabWithTokenHttp({
    url: '/api/v1/datalab/getChartList',
    method: 'post',
    data: props,
  } as TDatalabHttpConfig);

/**
 * 查询图表任务列表（前端 VO）。
 *
 * 对 `data_list` 每项执行 {@link transformChartListItemDtoToVo}（详情 transform + 列表扩展字段）。
 */
export const getChartListVo = async (
  props: IGetChartListRequest,
): Promise<IGetChartListResponseVo> => {
  const dto = await getChartList(props);
  return transformChartListResponseDtoToVo(dto);
};
